import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EntityHistoryButton from '../components/common/EntityHistoryButton';
import { ConfirmDialog } from '../components/common';
import activityAreaService, { ActivityArea, CreateActivityAreaDto, UpdateActivityAreaDto } from '../services/activityAreaService';

import './EntityManagement.css';

const ActivityAreaManagement: React.FC = () => {
  const [areas, setAreas] = useState<ActivityArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [currentArea, setCurrentArea] = useState<ActivityArea | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIcon, setFormIcon] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formHasPostal, setFormHasPostal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchAreas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await activityAreaService.getActivityAreas(page, rowsPerPage, searchTerm, showInactive ? undefined : true);
      setAreas(response.data);
      setTotalCount(response.pagination.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, rowsPerPage, searchTerm, showInactive]);

  useEffect(() => { fetchAreas(); }, [fetchAreas]);

  const handleSearch = () => { setSearchTerm(searchInput); setPage(1); };
  const handleKeyPress = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch(); };

  const openDialog = (area: ActivityArea | null) => {
    setCurrentArea(area);
    setFormName(area?.name || '');
    setFormDescription(area?.description || '');
    setFormIcon(area?.icon || '');
    setFormIsActive(area?.isActive ?? true);
    setFormHasPostal(area?.hasPostalDelivery ?? false);
    setFormError('');
    setDialogOpen(true);
  };

  const openDelete = (area: ActivityArea) => { setCurrentArea(area); setDeleteOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setCurrentArea(null); };
  const closeDelete = () => { setDeleteOpen(false); setCurrentArea(null); };

  const handleSave = async () => {
    if (!formName.trim()) { setFormError('نام حوزه فعالیت الزامی است'); return; }
    setSubmitLoading(true);
    try {
      const data = { name: formName, description: formDescription, icon: formIcon, isActive: formIsActive, hasPostalDelivery: formHasPostal };
      if (currentArea) await activityAreaService.updateActivityArea(currentArea._id, data as UpdateActivityAreaDto);
      else await activityAreaService.createActivityArea(data as CreateActivityAreaDto);
      closeDialog();
      fetchAreas();
    } catch (e: any) { setFormError(e.response?.data?.message || 'خطا در ذخیره'); }
    finally { setSubmitLoading(false); }
  };

  const handleDelete = async () => {
    if (!currentArea) return;
    setSubmitLoading(true);
    try { await activityAreaService.deleteActivityArea(currentArea._id); closeDelete(); fetchAreas(); }
    catch (e) { console.error(e); }
    finally { setSubmitLoading(false); }
  };

  const handleToggle = async (area: ActivityArea) => {
    try { await activityAreaService.toggleActivityAreaStatus(area._id); fetchAreas(); }
    catch (e) { console.error(e); }
  };

  const clearFilters = () => { setSearchTerm(''); setSearchInput(''); setShowInactive(false); setPage(1); };

  const activeCount = areas.filter(a => a.isActive).length;
  const inactiveCount = areas.filter(a => !a.isActive).length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, totalCount);
  const hasActiveFilters = searchTerm || showInactive;

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت حوزه‌های فعالیت</h1>
          <p className="entity-subtitle">حوزه‌ها برای دسته‌بندی انواع محصول استفاده می‌شوند</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchAreas} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={() => openDialog(null)}>
            <AddIcon fontSize="small" />
            افزودن حوزه
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><CategoryIcon /></div>
          <div>
            <p className="entity-stat-label">کل حوزه‌ها</p>
            <p className="entity-stat-value">{totalCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{activeCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال</p>
            <p className="entity-stat-value">{inactiveCount}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی حوزه..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نمایش</label>
            <label className="filter-toggle" style={{ height: 40 }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }}
              />
              نمایش غیرفعال‌ها
            </label>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {showInactive && (
              <span className="filter-tag">
                شامل غیرفعال‌ها
                <button onClick={() => setShowInactive(false)}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : areas.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><CategoryIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ حوزه‌ای یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>حوزه</th>
                  <th>توضیحات</th>
                  <th>ارسال پستی</th>
                  <th>وضعیت</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((area) => (
                  <tr key={area._id}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">
                          {area.icon ? <span style={{ fontSize: 20 }}>{area.icon}</span> : <CategoryIcon />}
                        </div>
                        <div className="entity-info">
                          <span className="entity-name">{area.name}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="entity-desc">{area.description || '-'}</span></td>
                    <td>
                      {area.hasPostalDelivery ? (
                        <span className="tag tag-sm tag-success"><LocalShippingIcon fontSize="inherit" /> دارد</span>
                      ) : (
                        <span className="tag tag-sm tag-inactive">ندارد</span>
                      )}
                    </td>
                    <td>
                      <span className={`tag tag-sm ${area.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {area.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Tooltip title="ویرایش" arrow>
                          <button className="action-btn edit" onClick={() => openDialog(area)}>
                            <EditIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="حذف" arrow>
                          <button className="action-btn delete" onClick={() => openDelete(area)}>
                            <DeleteIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title={area.isActive ? 'غیرفعال کردن' : 'فعال کردن'} arrow>
                          <button
                            className={`action-btn toggle ${area.isActive ? 'active' : ''}`}
                            onClick={() => handleToggle(area)}
                          >
                            {area.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </button>
                        </Tooltip>
                        <EntityHistoryButton
                          entityType="ActivityArea"
                          entityId={area._id}
                          size="small"
                          variant="text"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {areas.map((area) => (
                <div key={area._id} className={`entity-card ${!area.isActive ? 'inactive' : ''}`}>
                  <div className="entity-card-header">
                    <div className="entity-card-image">
                      {area.icon ? <span style={{ fontSize: 24 }}>{area.icon}</span> : <CategoryIcon />}
                    </div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{area.name}</div>
                      {area.description && <div className="entity-card-desc">{area.description}</div>}
                    </div>
                    <div className="entity-card-status">
                      <span className={`tag tag-sm ${area.isActive ? 'tag-active' : 'tag-inactive'}`}>
                        {area.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    <button className="entity-card-btn primary" onClick={() => openDialog(area)}>
                      <EditIcon fontSize="small" /> ویرایش
                    </button>
                    <button className="entity-card-btn" onClick={() => handleToggle(area)}>
                      {area.isActive ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
                      {area.isActive ? 'غیرفعال' : 'فعال'}
                    </button>
                    <button className="entity-card-btn danger" onClick={() => openDelete(area)}>
                      <DeleteIcon fontSize="small" /> حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalCount}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth className="activityarea-edit-dialog">
        <div className="edit-dialog-title">
          <span>{currentArea ? 'ویرایش حوزه فعالیت' : 'افزودن حوزه جدید'}</span>
          <button className="details-close" onClick={closeDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {formError && <div className="dialog-error">{formError}</div>}
          <div className="edit-form">
            {/* Basic Info */}
            <div className="edit-card">
              <div className="edit-card-header">اطلاعات اصلی</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field">
                    <label>نام حوزه <span style={{ color: '#dc2626' }}>*</span></label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="مثال: مراقبت از پوست و مو"
                    />
                  </div>
                  <div className="edit-field">
                    <label>آیکون (Emoji)</label>
                    <input
                      type="text"
                      value={formIcon}
                      onChange={(e) => setFormIcon(e.target.value)}
                      placeholder="🎮"
                    />
                  </div>
                  <div className="edit-field full">
                    <label>توضیحات</label>
                    <textarea
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="توضیحات حوزه فعالیت..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Settings */}
            <div className="edit-card">
              <div className="edit-card-header">تنظیمات</div>
              <div className="edit-card-body">
                <div className="edit-switches">
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">وضعیت فعال</div>
                      <div className="edit-switch-desc">حوزه در سایت نمایش داده شود</div>
                    </div>
                    <Switch size="small" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />
                  </label>
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">ارسال پستی</div>
                      <div className="edit-switch-desc">محصولات این حوزه قابلیت ارسال فیزیکی دارند</div>
                    </div>
                    <Switch size="small" checked={formHasPostal} onChange={(e) => setFormHasPostal(e.target.checked)} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={closeDialog}>انصراف</button>
          <button className="edit-btn submit" onClick={handleSave} disabled={submitLoading}>
            {submitLoading && <CircularProgress size={16} style={{ color: '#fff' }} />}
            {currentArea ? 'ذخیره تغییرات' : 'افزودن حوزه'}
          </button>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="حذف حوزه فعالیت"
        message={
          <span>
            آیا از حذف <strong>«{currentArea?.name}»</strong> اطمینان دارید؟
            <br />حذف این حوزه ممکن است روی محصولات مرتبط تأثیر بگذارد.
          </span>
        }
        cancelText="انصراف"
        confirmText="حذف حوزه"
        confirmColor="error"
        loading={submitLoading}
        onCancel={closeDelete}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ActivityAreaManagement;
